import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { AppContexts } from "../../../providers";
import FilterPanel from "../Common/FilterPanel";
import { buildExecutiveAnalyticsReport, formatDisplayValue } from "./analytics";

const KPI_CARD_CONFIG = [
  { key: "totalRevenue", label: "Tracked Revenue", type: "currency", section: "revenueTrends" },
  { key: "totalReservations", label: "Reservations", type: "number", section: "revenueTrends" },
  { key: "adr", label: "ADR", type: "currency", section: "revenueTrends" },
  { key: "repeatGuestRate", label: "Repeat Guest Rate", type: "percent", section: "repeatGuests" },
  { key: "cancellationRate", label: "Cancellation Rate", type: "percent", section: "bookingTrends" },
  { key: "averageLeadTime", label: "Average Lead Time", type: "days", section: "bookingTrends" },
];

const renderTextareaCell = (value) => (
  <textarea rows="1" disabled defaultValue={value || "N/A"} />
);

const renderMetricTable = (headers, rows, formatters = {}) => (
  <div className="uk-overflow-auto">
    <table className="uk-table uk-table-small uk-table-divider hms-table bi-table">
      <thead>
        <tr>
          {headers.map((header) => (
            <th key={header.key}>{header.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows?.length ? rows.map((row, index) => (
          <tr key={`${row.label || row.guestName || "row"}-${index}`}>
            {headers.map((header) => {
              const formatter = formatters[header.key];
              const value = row[header.key];
              return (
                <td key={header.key} className={header.className || ""}>
                  {formatter ? formatter(value, row) : value ?? "N/A"}
                </td>
              );
            })}
          </tr>
        )) : (
          <tr>
            <td colSpan={headers.length} className="no-results">No data available</td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

const Section = ({ title, purpose, children, hideTitle = false }) => (
  <section className="bi-panel">
    <div className="bi-section-header">
      {!hideTitle ? <h3>{title}</h3> : null}
      <p>{purpose}</p>
    </div>
    <div className="bi-report-body">
      {children}
    </div>
  </section>
);

const SECTION_CONFIG = {
  "performance-overview": {
    title: "Overview",
    renderKey: "summary",
  },
  "revenue-trends": {
    title: "Revenue Trends",
    renderKey: "revenueTrends",
  },
  "booking-trends": {
    title: "Booking Trends",
    renderKey: "bookingTrends",
  },
  "channel-performance": {
    title: "Channel Performance",
    renderKey: "channelPerformance",
  },
  "property-performance": {
    title: "Property Performance",
    renderKey: "propertyPerformance",
  },
  "repeat-guests": {
    title: "Repeat Guests",
    renderKey: "repeatGuests",
  },
  "revenue-opportunities": {
    title: "Revenue Opportunities",
    renderKey: "revenueOpportunities",
  },
  "executive-analytics": {
    title: "Performance Analytics",
    renderKey: "all",
  },
};

const ExecutiveAnalytics = ({ activeReport = "all" }) => {
  const [copied, setCopied] = useState(false);
  const {
    loading,
    startDate,
    endDate,
    bookingIntelligence,
    getBookingIntelligence,
  } = useContext(AppContexts.ReportsContext);

  const getFn = useCallback(
    (start, end) => {
      if (!start || !end) return;
      getBookingIntelligence(start, end);
    },
    [getBookingIntelligence],
  );

  useEffect(() => {
    if (SECTION_CONFIG[activeReport]) {
      getFn(startDate, endDate);
    }
  }, [activeReport, getFn, startDate, endDate]);

  const payload = useMemo(
    () => buildExecutiveAnalyticsReport(bookingIntelligence, { startDate, endDate }),
    [bookingIntelligence, startDate, endDate],
  );

  const copyJson = useCallback(async () => {
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }, [payload]);

  const hasData = payload.metadata.totalReservations > 0;
  const revenue = payload.reports.revenueTrends;
  const booking = payload.reports.bookingTrends;
  const channels = payload.reports.channelPerformance;
  const properties = payload.reports.propertyPerformance;
  const loyalty = payload.reports.repeatGuests;
  const opportunities = payload.reports.revenueOpportunities;
  const selectedReport = SECTION_CONFIG[activeReport] || SECTION_CONFIG["executive-analytics"];
  const showOverview = selectedReport.renderKey === "all" || selectedReport.renderKey === "summary";
  const showSummaryOnly = selectedReport.renderKey === "summary";
  const hideSectionTitle = !showOverview;
  const showSection = (key) => selectedReport.renderKey === "all" || selectedReport.renderKey === key;

  return (
    <li className="reports-tab-content">
      <h2>{selectedReport.title}</h2>
      <div className="uk-card hms-left-tabs-content-card uk-padding-remove">
        <FilterPanel onSubmit={getFn} />
      </div>

      {loading > 0 && !hasData ? (
        <div className="uk-card hms-left-tabs-content-card bi-empty-state">
          <p>Generating executive analytics...</p>
        </div>
      ) : null}

      {!hasData && loading === 0 ? (
        <div className="uk-card hms-left-tabs-content-card bi-empty-state">
          <p>No reservation data is available for this date range.</p>
        </div>
      ) : null}

      {hasData ? (
        <>
          {showOverview ? (
            <>
              <div className="overview-kpi-row bi-kpi-row">
                {KPI_CARD_CONFIG.map((card) => {
                  const source = payload.reports[card.section]?.kpis || {};
                  return (
                    <div className="overview-kpi-card" key={card.key}>
                      <div className="kpi-label">{card.label}</div>
                      <div className="kpi-total">{formatDisplayValue(source[card.key], card.type)}</div>
                    </div>
                  );
                })}
              </div>

              <section className="bi-panel">
                <div className="bi-section-header">
                  <h3>AI Executive Summary</h3>
                  <p>Top management findings, risks, and actions from the selected reservation window.</p>
                </div>
                <div className="bi-report-body">
                  <div className="bi-summary-grid">
                    <div>
                      <h4>Top 10 Findings</h4>
                      <ol className="bi-insights">
                        {payload.executiveSummary.topFindings.map((finding, index) => (
                          <li key={index}>{finding}</li>
                        ))}
                      </ol>
                    </div>
                    <div>
                      <h4>Management Actions</h4>
                      <ol className="bi-insights">
                        {payload.executiveSummary.recommendedManagementActions.map((action, index) => (
                          <li key={index}>{action}</li>
                        ))}
                      </ol>
                    </div>
                  </div>
                  <div className="bi-quality-grid">
                    <div className="overview-metric-card">
                      <div className="metric-title">Revenue Coverage</div>
                      <div className="metric-total">{formatDisplayValue(payload.metadata.revenueCoverageRate, "percent")}</div>
                    </div>
                    <div className="overview-metric-card">
                      <div className="metric-title">Source Coverage</div>
                      <div className="metric-total">{formatDisplayValue(payload.metadata.sourceCoverageRate, "percent")}</div>
                    </div>
                    <div className="overview-metric-card">
                      <div className="metric-title">Location Coverage</div>
                      <div className="metric-total">{formatDisplayValue(payload.metadata.locationCoverageRate, "percent")}</div>
                    </div>
                  </div>
                </div>
              </section>
            </>
          ) : null}

          {!showSummaryOnly && showSection("revenueTrends") ? (
          <Section title="Revenue Trends" purpose={revenue.purpose} hideTitle={hideSectionTitle}>
            {renderMetricTable(
              [
                { key: "label", label: "Month" },
                { key: "value", label: "Revenue" },
                { key: "reservations", label: "Reservations" },
                { key: "adr", label: "ADR" },
              ],
              revenue.breakdowns.revenueByMonth,
              {
                value: (value) => formatDisplayValue(value, "currency"),
                adr: (value) => formatDisplayValue(value, "currency"),
              },
            )}
            {renderMetricTable(
              [
                { key: "label", label: "Channel" },
                { key: "value", label: "Revenue" },
                { key: "reservationCount", label: "Reservations" },
                { key: "share", label: "Revenue Share" },
              ],
              revenue.breakdowns.revenueByBookingSource,
              {
                value: (value) => formatDisplayValue(value, "currency"),
                share: (value) => formatDisplayValue(value, "percent"),
              },
            )}
            {renderMetricTable(
              [
                { key: "label", label: "Property" },
                { key: "value", label: "Revenue" },
                { key: "reservationCount", label: "Reservations" },
                { key: "share", label: "Revenue Share" },
              ],
              revenue.breakdowns.revenueByProperty.slice(0, 10),
              {
                value: (value) => formatDisplayValue(value, "currency"),
                share: (value) => formatDisplayValue(value, "percent"),
              },
            )}
            <div className="bi-insights-block">
              <h4 className="bi-insights-title">Executive Insights</h4>
              <ol className="bi-insights">
                {revenue.insights.map((item, index) => <li key={index}>{item}</li>)}
              </ol>
            </div>
          </Section>
          ) : null}

          {!showSummaryOnly && showSection("bookingTrends") ? (
          <Section title="Booking Trends" purpose={booking.purpose} hideTitle={hideSectionTitle}>
            {renderMetricTable(
              [
                { key: "label", label: "Lead Time Bucket" },
                { key: "value", label: "Reservations" },
              ],
              booking.breakdowns.leadTimeDistribution,
            )}
            {renderMetricTable(
              [
                { key: "label", label: "Stay Length Bucket" },
                { key: "value", label: "Reservations" },
              ],
              booking.breakdowns.stayLengthDistribution,
            )}
            {renderMetricTable(
              [
                { key: "label", label: "Season" },
                { key: "reservationCount", label: "Reservations" },
                { key: "averageLeadTime", label: "Average Lead Time" },
                { key: "averageStayLength", label: "Average Stay" },
                { key: "revenue", label: "Revenue" },
              ],
              booking.breakdowns.seasonalBookingPatterns,
              {
                averageLeadTime: (value) => formatDisplayValue(value, "days"),
                averageStayLength: (value) => formatDisplayValue(value, "nights"),
                revenue: (value) => formatDisplayValue(value, "currency"),
              },
            )}
            <div className="bi-insights-block">
              <h4 className="bi-insights-title">Executive Insights</h4>
              <ol className="bi-insights">
                {booking.insights.map((item, index) => <li key={index}>{item}</li>)}
              </ol>
            </div>
          </Section>
          ) : null}

          {!showSummaryOnly && showSection("channelPerformance") ? (
          <Section title="Channel Performance" purpose={channels.purpose} hideTitle={hideSectionTitle}>
            {renderMetricTable(
              [
                { key: "label", label: "Channel" },
                { key: "revenue", label: "Revenue" },
                { key: "reservationCount", label: "Reservations" },
                { key: "adr", label: "ADR" },
                { key: "averageStayLength", label: "Average Stay" },
                { key: "averageLeadTime", label: "Average Lead Time" },
                { key: "repeatGuestRate", label: "Repeat Guest %" },
              ],
              channels.channels,
              {
                revenue: (value) => formatDisplayValue(value, "currency"),
                adr: (value) => formatDisplayValue(value, "currency"),
                averageStayLength: (value) => formatDisplayValue(value, "nights"),
                averageLeadTime: (value) => formatDisplayValue(value, "days"),
                repeatGuestRate: (value) => formatDisplayValue(value, "percent"),
              },
            )}
            <div className="bi-insights-block">
              <h4 className="bi-insights-title">Executive Insights</h4>
              <ol className="bi-insights">
                {channels.insights.map((item, index) => <li key={index}>{item}</li>)}
              </ol>
            </div>
          </Section>
          ) : null}

          {!showSummaryOnly && showSection("propertyPerformance") ? (
          <Section title="Property Performance" purpose={properties.purpose} hideTitle={hideSectionTitle}>
            {renderMetricTable(
              [
                { key: "label", label: "Property" },
                { key: "revenue", label: "Revenue" },
                { key: "reservationCount", label: "Reservations" },
                { key: "adr", label: "ADR" },
                { key: "averageStayLength", label: "Average Stay" },
                { key: "averageLeadTime", label: "Lead Time" },
                { key: "repeatGuestRate", label: "Repeat Guest %" },
                { key: "cancellationRate", label: "Cancellation Rate" },
              ],
              properties.properties.slice(0, 15),
              {
                revenue: (value) => formatDisplayValue(value, "currency"),
                adr: (value) => formatDisplayValue(value, "currency"),
                averageStayLength: (value) => formatDisplayValue(value, "nights"),
                averageLeadTime: (value) => formatDisplayValue(value, "days"),
                repeatGuestRate: (value) => formatDisplayValue(value, "percent"),
                cancellationRate: (value) => formatDisplayValue(value, "percent"),
              },
            )}
            <div className="bi-insights-block">
              <h4 className="bi-insights-title">Executive Insights</h4>
              <ol className="bi-insights">
                {properties.insights.map((item, index) => <li key={index}>{item}</li>)}
              </ol>
            </div>
          </Section>
          ) : null}

          {!showSummaryOnly && showSection("repeatGuests") ? (
          <Section title="Repeat Guests" purpose={loyalty.purpose} hideTitle={hideSectionTitle}>
            {renderMetricTable(
              [
                { key: "label", label: "Guest Segment" },
                { key: "value", label: "Guests" },
              ],
              loyalty.breakdowns.newVsReturningGuests,
            )}
            {renderMetricTable(
              [
                { key: "label", label: "Channel" },
                { key: "value", label: "Repeat Revenue" },
              ],
              loyalty.breakdowns.repeatRevenueByChannel,
              {
                value: (value) => formatDisplayValue(value, "currency"),
              },
            )}
            {renderMetricTable(
              [
                { key: "guestName", label: "Guest" },
                { key: "stayCount", label: "Stays" },
                { key: "revenue", label: "Revenue" },
                { key: "averageTimeBetweenVisits", label: "Avg Days Between Visits" },
              ],
              loyalty.breakdowns.topReturningGuests,
              {
                revenue: (value) => formatDisplayValue(value, "currency"),
                averageTimeBetweenVisits: (value) => formatDisplayValue(value, "days"),
              },
            )}
            <div className="bi-insights-block">
              <h4 className="bi-insights-title">Executive Insights</h4>
              <ol className="bi-insights">
                {loyalty.insights.map((item, index) => <li key={index}>{item}</li>)}
              </ol>
            </div>
          </Section>
          ) : null}

          {!showSummaryOnly && showSection("revenueOpportunities") ? (
          <Section title="Revenue Opportunities" purpose={opportunities.purpose} hideTitle={hideSectionTitle}>
            {renderMetricTable(
              [
                { key: "opportunityName", label: "Opportunity", className: "message" },
                { key: "estimatedAnnualImpact", label: "Estimated Annual Impact" },
                { key: "confidenceLevel", label: "Confidence" },
                { key: "reasoning", label: "Reasoning", className: "message" },
                { key: "recommendedAction", label: "Recommended Action", className: "message" },
              ],
              opportunities.opportunities,
              {
                estimatedAnnualImpact: (value) => formatDisplayValue(value, "currency"),
                opportunityName: (value) => renderTextareaCell(value),
                reasoning: (value) => renderTextareaCell(value),
                recommendedAction: (value) => renderTextareaCell(value),
              },
            )}
          </Section>
          ) : null}

          {selectedReport.renderKey === "all" ? (
            <section className="bi-panel">
              <div className="bi-section-header bi-json-header">
                <div>
                  <h3>JSON Payload</h3>
                  <p>The same structured payload can back KPI cards, charts, tables, PDF exports, and AI narrative rendering.</p>
                </div>
                <button type="button" className="uk-button hms-btn hms-red-btn" onClick={copyJson}>
                  {copied ? "Copied" : "Copy JSON"}
                </button>
              </div>
              <pre className="bi-json-payload">{JSON.stringify(payload, null, 2)}</pre>
            </section>
          ) : null}
        </>
      ) : null}
    </li>
  );
};

export default ExecutiveAnalytics;